<template>
  <v-dialog
    v-model="show"
    persistent
    @keydown.esc="hide"
    width="500"
    v-if="role"
  >
    <template v-slot:activator="{ on }">
      <v-btn v-on="on" small class="primary accent-4 white--text mr-2 my-1">
        {{ $t(`profile.assignment.${name}.button`) }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="headline grey lighten-2" primary-title>{{
        $t(`profile.assignment.${name}.dialog`)
      }}</v-card-title>

      <v-card-text>
        <div v-if="importSettings" @click="changeImportSettings">
          <v-checkbox
            v-model="importIgnoredCategories"
            :value="category"
            :label="$t('profile.assignment.changeDepartments.ignoreCategory')"
          />
        </div>
        <v-autocomplete
          class="mt-0 pt-0"
          v-model="newCategoryTags"
          :items="allCategoryTags"
          required
          chips
          :deletable-chips="
            !!Array.isArray(newCategoryTags) && !!newCategoryTags.length
          "
          :item-text="getTagDescription"
          return-object
          :multiple="multiple"
          :filter="customFilter"
        >
          <template v-slot:item="{ item, attrs }">
            <v-list-item-action>
              <v-checkbox :input-value="attrs.inputValue"></v-checkbox>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{
                getTagDescription(item)
              }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.explanation">
                <span> {{ item.explanation }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              close
              @click="data.select"
              @click:close="remove(data.item)"
            >
              <span
                :style="
                  hasSource(data.item) ? 'text-decoration: underline;' : ''
                "
                >{{ getTagDescription(data.item) }}
              </span>
            </v-chip>
          </template>
        </v-autocomplete>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="grey" text @click="hide">{{ $t("dialog.cancel") }}</v-btn>

        <v-btn color="primary" text @click="save" :loading="loading">{{
          $t("dialog.assign")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TagsQuery from "@/graphql/Tags.gql";
import ChangeEmployeeTagsQuery from "@/graphql/ChangeEmployeeTags.gql";
import ChangeImportIgnoredCategoriesQuery from "@/graphql/ChangeImportIgnoredCategories.gql";
import ImportIgnoredCategoriesQuery from "@/graphql/ImportIgnoredCategories.gql";

import { getTagDescription } from "@/utils/tag";

export default {
  props: [
    "id",
    "tags",
    "multiple",
    "category",
    "name",
    "importSettings",
    "role",
  ],

  data() {
    return {
      show: false,
      newCategoryTags: [],
      loading: false,
    };
  },
  mounted() {
    this.updateNewCategoryTags();
  },

  watch: {
    tags() {
      this.updateNewCategoryTags();
    },
  },

  methods: {
    getTagDescription: getTagDescription,

    hasSource(tag) {
      const t = this.tags.find((t) => t.id == tag.id);
      if (!t) return false;
      return t.source == "kronos";
    },
    remove(item) {
      if (!this.multiple) {
        this.newCategoryTags = [];
        return;
      }

      const index = this.newCategoryTags.map((x) => x.id).indexOf(item.id);
      if (index >= 0) this.newCategoryTags.splice(index, 1);
    },
    updateNewCategoryTags() {
      if (this.multiple) {
        this.newCategoryTags =
          this.tags && this.tags.filter((t) => t.category === this.category);
        return;
      }
      this.newCategoryTags =
        this.tags && this.tags.find((t) => t.category === this.category);
    },
    hide() {
      this.show = false;
    },
    save() {
      this.loading = true;
      const newTagIds = Array.isArray(this.newCategoryTags)
        ? this.newCategoryTags.map((t) => t.id)
        : [this.newCategoryTags.id];
      this.$apollo
        .mutate({
          mutation: ChangeEmployeeTagsQuery,
          variables: {
            id: this.id,
            tags: newTagIds,
            category: this.category,
          },
        })
        .then((res) => {
          this.$emit("update:tags", res.data.employee.tags);
          this.$emit("refresh");
          this.hide();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeImportSettings() {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: ChangeImportIgnoredCategoriesQuery,
          variables: {
            employeeId: this.id,
            categories: Array.isArray(this.importIgnoredCategories)
              ? this.importIgnoredCategories
              : [this.importIgnoredCategories],
          },
        })
        .then((res) => {
          this.importIgnoredCategories = res.data.categories;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    customFilter(item, queryText) {
      const searchText = queryText.toLowerCase();
      const text = `${item.name} | ${item.description || ""}`.toLowerCase();
      return text.includes(searchText);
    },
  },

  apollo: {
    importIgnoredCategories: {
      query: ImportIgnoredCategoriesQuery,
      variables() {
        return {
          employeeId: this.id,
        };
      },
      skip() {
        return !this.show || !this.importSettings;
      },
    },
    allCategoryTags: {
      query: TagsQuery,
      update(data) {
        return data.tags.filter((t) => t.category === this.category);
      },
      skip() {
        return !this.show;
      },
      fetchPolicy: "cache-and-network",
    },
  },
};
</script>
